import type { CSSProperties } from "react";

/**
 * The four leaves, bottom to top. `x`/`y` is the pivot where the leaf joins
 * the stem (viewBox units), `dir` the sign of its clockwise rotation, `at` the
 * fraction of the stem's growth at which it opens. `softFirst` keeps the paint
 * order of the static logo.
 */
const LEAVES = [
  {
    x: 590,
    y: 888,
    dir: 1,
    at: 0.27,
    softFirst: false,
    outline:
      "M589.972 887.693C523.395 798.924 390.241 776.732 323.664 643.578C434.626 643.578 545.587 724.544 589.972 791.121",
    soft: "M589.972 887.693C523.395 798.924 390.241 776.732 323.664 643.578C543.671 747.154 529.412 779.473 589.972 834.607",
  },
  {
    x: 587,
    y: 725,
    dir: 1,
    at: 0.41,
    softFirst: false,
    outline:
      "M587.497 724.631C498.728 613.67 318.374 576.999 251.797 421.652C362.758 421.652 520.92 534.436 587.497 623.205",
    soft: "M587.497 724.631C498.728 613.67 318.374 576.998 251.797 421.652C336.918 487.647 512.299 584.606 587.497 655.424",
  },
  {
    x: 590,
    y: 666,
    dir: -1,
    at: 0.46,
    softFirst: true,
    outline:
      "M590.492 665.771C679.261 599.194 790.223 554.809 856.8 443.848C768.031 443.848 634.877 532.617 590.492 599.194",
    soft: "M590.492 665.771C679.261 599.194 790.223 554.809 856.8 443.848C750.895 500.478 644.905 573.672 590.492 634.034",
  },
  {
    x: 592,
    y: 534,
    dir: -1,
    at: 0.56,
    softFirst: false,
    outline:
      "M592.188 533.612C680.957 444.843 860.186 377.271 926.763 244.117C815.801 244.117 662.709 287.416 596.132 398.378",
    soft: "M592.188 533.612C680.957 444.843 860.186 377.271 926.763 244.117C775.136 308.463 669.147 374.053 592.188 465.783",
  },
];

/**
 * The Caliu logo, alive: same paths as `CaliuLogo` with thinner strokes.
 * `idle` is the grown plant swaying; `intro` first plays the welcome opening
 * (blinking caret, growing stem, unfurling leaves) and then idles the same
 * way. Motion is CSS only (`onboarding-logo-*` in index.css) and inert under
 * reduced motion. Uses `currentColor`.
 */
export function AnimatedCaliuLogo({
  size,
  className = "",
  mode,
}: {
  size: number;
  className?: string;
  mode: "intro" | "idle";
}) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 1154 1154"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={[
        "onboarding-logo",
        mode === "intro" ? "onboarding-logo-intro" : "",
        className,
      ].join(" ")}
      aria-hidden="true"
    >
      <g className="onboarding-logo-sway">
        {LEAVES.map((leaf, index) => {
          const soft = (
            <path d={leaf.soft} fill="currentColor" fillOpacity={0.16} />
          );
          return (
            <g
              key={leaf.at}
              className="onboarding-logo-leaf"
              // Custom properties read by the keyframes (pivot, side, rhythm).
              style={
                {
                  "--ox": `${leaf.x}px`,
                  "--oy": `${leaf.y}px`,
                  "--dir": leaf.dir,
                  "--i": index,
                  "--at": leaf.at,
                } as CSSProperties
              }
            >
              <g className="onboarding-logo-flutter">
                {leaf.softFirst && soft}
                <path
                  d={leaf.outline}
                  fill="currentColor"
                  fillOpacity={0.28}
                  stroke="currentColor"
                  strokeWidth={22}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                {!leaf.softFirst && soft}
              </g>
            </g>
          );
        })}
        <path
          className="onboarding-logo-stem"
          d="M592.271 1024.5C592.271 1024.5 580.226 316.565 610.502 165"
          pathLength={1}
          stroke="currentColor"
          strokeWidth={24}
          strokeLinecap="round"
          strokeDasharray="1"
        />
      </g>
    </svg>
  );
}
